import React from 'react';
// 1. Import motion for animations
import { motion } from 'framer-motion';

// --- Animation Variants for a Polished Feel ---

// Variant for the main container to fade in on scroll
const footerVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: "easeOut",
      staggerChildren: 0.15, // Stagger the animation of direct children (the columns)
    },
  },
};

// Variant for each column to fade and slide up
const columnVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

// Variant for staggering list items (like links)
const listStagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

// Variant for individual list items
const listItemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0 },
};

export default function Footer() {
  const exploreLinks = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Collaborate', href: '/collaborate' },
    { name: 'Contact', href: '/contact' },
    { name: 'Request a Demo', href: '/request-demo' },
  ];

  const legalLinks = [
    { name: 'Privacy Policy', href: '/PrivacyPolicy' },
    { name: 'Terms & Conditions', href: '/TermsConditions' },
    { name: 'Cookie Policy', href: '/cookiepolicy' },
  ];
  
  const socialLinks = [
    { name: 'LinkedIn', href: 'https://www.linkedin.com/company/shigjeta/', icon: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M4.98 3.5c0 1.381-1.11 2.5-2.48 2.5s-2.48-1.119-2.48-2.5c0-1.38 1.11-2.5 2.48-2.5s2.48 1.12 2.48 2.5zm.02 4.5h-5v16h5v-16zm7.982 0h-4.968v16h4.969v-8.399c0-4.67 6.029-5.052 6.029 0v8.399h4.988v-10.131c0-7.88-8.922-7.593-11.018-3.714v-2.155z" /></svg> },
    { name: 'Facebook', href: 'https://www.facebook.com/shigjeta.agency', icon: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M22.675 0h-21.35C.593 0 0 .593 0 1.325v21.351C0 23.407.593 24 1.325 24H12.82v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.894-4.788 4.659-4.788 1.325 0 2.464.099 2.795.143v3.24h-1.918c-1.504 0-1.796.715-1.796 1.764v2.314h3.59l-.467 3.622h-3.123V24h6.116C23.407 24 24 23.407 24 22.676V1.325C24 .593 23.407 0 22.675 0z" /></svg> }
  ];

  return (
    <motion.footer
      className="bg-[#0E1720] text-gray-300 py-16 mt-16 relative overflow-hidden"
      variants={footerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
    >
      <div className="absolute inset-0 opacity-5 z-0">
        <svg className="w-full h-full" viewBox="0 0 1400 400" preserveAspectRatio="xMidYMid slice">
          <path fill="#16a085" d="M0,400L80,373.3C160,347,320,293,480,280C640,267,800,293,960,306.7C1120,320,1280,320,1360,320L1400,320L1400,0L1360,0C1280,0,1120,0,960,0C800,0,640,0,480,0C320,0,160,0,80,0L0,0Z" />
        </svg>
      </div>

      <div className="max-w-7xl xl:max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8 text-center md:text-left">
          
          {/* Logo and Description */}
          <motion.div variants={columnVariants} className="flex flex-col items-center md:items-start">
            <motion.a href="/" whileHover={{ scale: 1.05, rotate: 5 }} transition={{ type: 'spring', stiffness: 300 }}>
              <img src="/images/void.png" alt="Void Support System Logo" className="h-12 mb-4"/>
            </motion.a>
            <p className="text-gray-400 text-sm leading-relaxed">
              Built in silence, trusted abroad.<br />
              Void brings the future of support to where it’s needed most.
            </p>
          </motion.div>

          {/* Main Pages */}
          <motion.div variants={columnVariants}>
            <h3 className="text-lg font-semibold text-white mb-4">Explore</h3>
            <motion.ul className="space-y-3 text-sm" variants={listStagger}>
              {exploreLinks.map((item) => (
                <motion.li key={item.name} variants={listItemVariants}>
                  <motion.a href={item.href} className="hover:text-[#16a085] transition-colors duration-300" whileHover={{ x: 5 }}>
                    {item.name}
                  </motion.a>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>

          {/* Legal Pages */}
          <motion.div variants={columnVariants}>
            <h3 className="text-lg font-semibold text-white mb-4">Legal</h3>
            <motion.ul className="space-y-3 text-sm" variants={listStagger}>
              {legalLinks.map((item) => (
                <motion.li key={item.name} variants={listItemVariants}>
                  <motion.a href={item.href} className="hover:text-[#16a085] transition-colors duration-300" whileHover={{ x: 5 }}>
                    {item.name}
                  </motion.a>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div variants={columnVariants}>
            <h3 className="text-lg font-semibold text-white mb-4">Stay Connected</h3>
            <ul className="space-y-3 text-sm mb-6">
              <motion.li variants={listItemVariants}>
                <motion.a href="mailto:contact@shigjeta.com" className="hover:text-[#16a085] transition-colors duration-300 flex items-center justify-center md:justify-start" whileHover={{ x: 5 }}>
                  <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                  contact@shigjeta.com
                </motion.a>
              </motion.li>
              <motion.li variants={listItemVariants}>
                <motion.a href="tel:+38346192921" className="hover:text-[#16a085] transition-colors duration-300 flex items-center justify-center md:justify-start" whileHover={{ x: 5 }}>
                  <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                  +383 (46) 192-921
                </motion.a>
              </motion.li>
              <motion.li variants={listItemVariants} className="flex items-center justify-center md:justify-start">
                <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                Kaçanik, Kosovo
              </motion.li>
            </ul>
          </motion.div>
        </div>

        {/* Social Media Links */}
        <motion.div className="mt-12 flex justify-center space-x-6" variants={columnVariants}>
          {socialLinks.map((item) => (
            <motion.a
              key={item.name}
              href={item.href}
              className="text-gray-400 hover:text-white transition-colors duration-300"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ y: -3, scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              title={item.name}
            >
              {item.icon}
            </motion.a>
          ))}
        </motion.div>

        {/* Bottom Bar */}
        <motion.div className="mt-12 pt-8 border-t border-gray-800 text-sm text-center" variants={columnVariants}>
          <p>© {new Date().getFullYear()} Void Support System. Engineered for global scale, empowering the future of support.</p>
        </motion.div>
      </div>
    </motion.footer>
  );
}